import React from 'react'

import { EuiFlexGroup, EuiFlexItem, EuiPanel, EuiSpacer, EuiSuperSelect, EuiEmptyPrompt } from '@elastic/eui'

import { ReportCampaignDimension, ReportCampaign } from 'api/interfaces'
import ReportDataGrid, { ReportDataGridColumn, ReportDataGridSorting } from 'features/reports/ReportDataGrid'
import { formatNumber, formatPercentage } from 'utils/Formatters'

import { LoadingComponent, TitleComponent } from './FormattingComponents'

interface GeoPerformanceProps {
  placementData?: ReportCampaignDimension[]
  campaignData?: ReportCampaign[]
  isLoading: boolean
  onDimensionChange?: (dimension: string) => void
}

interface AggregatedGeoData {
  location: string
  views: number
  clicks: number
  ctr: number
}

const dimensionOptions = [
  { value: 'State', inputDisplay: 'State' },
  { value: 'City', inputDisplay: 'City' },
  { value: 'DMA', inputDisplay: 'DMA' }
]

const GeoPerformance: React.FC<GeoPerformanceProps> = ({ placementData, campaignData, isLoading, onDimensionChange }) => {
  const [selectedDimension, setSelectedDimension] = React.useState('City')

  const title = 'Geographic Data'

  const handleDimensionChange = (value: string) => {
    setSelectedDimension(value)
    if (onDimensionChange) {
      onDimensionChange(value)
    }
  }

  if (isLoading) {
    return <LoadingComponent title={title} />
  }

  if (!placementData || placementData.length <= 0) {
    return (
      <EuiPanel>
        <EuiFlexGroup justifyContent='spaceBetween' alignItems='center'>
          <EuiFlexItem grow={false}>
            <TitleComponent title={title} />
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiSuperSelect options={dimensionOptions} valueOfSelected={selectedDimension} onChange={handleDimensionChange} aria-label='Select dimension' />
          </EuiFlexItem>
        </EuiFlexGroup>
        <EuiSpacer size='m' />
        <EuiEmptyPrompt title={<h3>No data available</h3>} body='No geographic data available for the selected period.' />
      </EuiPanel>
    )
  }

  const aggregateGeoData = (data: ReportCampaignDimension[], totalCampaignData?: ReportCampaign[]): AggregatedGeoData[] => {
    const locationMap = new Map<string, AggregatedGeoData>()

    data.forEach(item => {
      const location = item.dimensionValue
      const existing = locationMap.get(location)

      if (existing) {
        existing.views += item.views
        existing.clicks += item.clicks
      } else {
        locationMap.set(location, {
          location,
          views: item.views,
          clicks: item.clicks,
          ctr: 0
        })
      }
    })

    const aggregatedLocations = Array.from(locationMap.values())
      .filter(location => location.views > 0)
      .map(location => ({
        ...location,
        ctr: location.views > 0 ? (location.clicks / location.views) * 100 : 0
      }))

    if (totalCampaignData && totalCampaignData.length > 0) {
      const totalCampaignViews = totalCampaignData.reduce((sum, item) => sum + item.views, 0)
      const totalCampaignClicks = totalCampaignData.reduce((sum, item) => sum + item.clicks, 0)

      const totalLocationViews = aggregatedLocations.reduce((sum, location) => sum + location.views, 0)
      const totalLocationClicks = aggregatedLocations.reduce((sum, location) => sum + location.clicks, 0)

      const notSetViews = totalCampaignViews - totalLocationViews
      const notSetClicks = totalCampaignClicks - totalLocationClicks

      if (notSetViews > 0) {
        aggregatedLocations.push({
          location: '(not set)',
          views: notSetViews,
          clicks: notSetClicks,
          ctr: notSetViews > 0 ? (notSetClicks / notSetViews) * 100 : 0
        })
      }
    }

    return aggregatedLocations
  }

  const aggregatedData = aggregateGeoData(placementData, campaignData)

  if (aggregatedData.length <= 0) {
    return (
      <EuiPanel>
        <EuiFlexGroup justifyContent='spaceBetween' alignItems='center'>
          <EuiFlexItem grow={false}>
            <TitleComponent title={title} />
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiSuperSelect options={dimensionOptions} valueOfSelected={selectedDimension} onChange={handleDimensionChange} aria-label='Select dimension' />
          </EuiFlexItem>
        </EuiFlexGroup>
        <EuiSpacer size='m' />
        <EuiEmptyPrompt title={<h3>No data available</h3>} body='No geographic data available for the selected period.' />
      </EuiPanel>
    )
  }

  const isInternetCampaign = aggregatedData.some(location => location.clicks > 0)

  const defaultSorting: ReportDataGridSorting[] = [
    {
      id: 'views',
      direction: 'desc'
    }
  ]

  const columns: ReportDataGridColumn[] = [
    {
      id: 'location',
      displayAsText: selectedDimension,
      schema: 'string'
    },
    {
      id: 'views',
      displayAsText: 'Views',
      schema: 'numeric',
      render: (value: number) => formatNumber(value, 0, 0)
    }
  ]

  if (isInternetCampaign) {
    columns.push(
      {
        id: 'clicks',
        displayAsText: 'Clicks',
        schema: 'numeric',
        render: (value: number) => formatNumber(value, 0, 0)
      },
      {
        id: 'ctr',
        displayAsText: 'CTR %',
        schema: 'numeric',
        render: (value: number) => formatPercentage(value / 100, 3, 3)
      }
    )
  }

  return (
    <EuiPanel>
      <EuiFlexGroup justifyContent='spaceBetween' alignItems='center'>
        <EuiFlexItem grow={false}>
          <TitleComponent title={title} />
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiSuperSelect options={dimensionOptions} valueOfSelected={selectedDimension} onChange={handleDimensionChange} aria-label='Select dimension' />
        </EuiFlexItem>{' '}
      </EuiFlexGroup>
      <EuiSpacer size='m' />
      <ReportDataGrid data={aggregatedData} columns={columns} defaultSorting={defaultSorting} />
    </EuiPanel>
  )
}

export default GeoPerformance
